// Explore — natural-language SOQL + chart + reshape, wired to the
// production backend. Mirrors the prod app/frontend/src/pages/Explorer.jsx
// behavior: ask a question → POST /api/explorer/query → render chart +
// table + SOQL → optional reshape via POST /api/reshape/preview, then
// /api/reshape/reshape with progress polling on /api/jobs/:id.

import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { MissionControlHero } from "@/components/hero/MissionControlHero";
import { apiCall, apiSoft } from "@/lib/api/http";
import { useAuth } from "@/hooks/useAuth";
import { useUiStore } from "@/lib/store/uiStore";

const QUICK_CHIPS: { label: string; query: string }[] = [
  { label: "Win rate by month", query: "Show win rate trend by month" },
  { label: "Pipeline by stage", query: "Show opportunity count by stage" },
  { label: "Cases by priority", query: "Case count by priority" },
  { label: "Top accounts by revenue", query: "Top 10 accounts by opportunity amount" },
];
const SUGGESTED: { label: string; query: string }[] = [
  ...QUICK_CHIPS,
  { label: "Cases by product", query: "How many cases per product?" },
  { label: "CSAT trend", query: "What is the average CSAT score by month?" },
  { label: "Revenue by region", query: "Total opportunity amount by region" },
  { label: "Cases by status", query: "Case count by status" },
];

const COLORS = [
  "#066AFE",
  "#04E1CB",
  "#FF538A",
  "#FCC003",
  "#D17DFE",
  "#4A6785",
  "#00B4D8",
  "#E63946",
];

const HISTORY_KEY = "qbranch.v2.explorer.history";
const HISTORY_MAX = 20;

interface ExplorerResult {
  explanation?: string | null;
  error?: string | null;
  chart_type?: "bar" | "line" | "pie" | string | null;
  chart_data?: Record<string, unknown>[] | null;
  table_data?: Record<string, unknown>[] | null;
  narrative?: string | null;
  soql?: string | null;
  x_key?: string | null;
  y_key?: string | null;
}

interface SavedQuery {
  id: string;
  name: string;
  nl_query?: string | null;
  soql?: string | null;
  chart_type?: string | null;
}

interface ReshapePreview {
  interpretation: string;
  opp_count: number;
  case_count: number;
  overrides: Record<string, unknown>;
}

interface JobStatus {
  status: "queued" | "running" | "completed" | "failed";
  current_step?: string | null;
  error_message?: string | null;
  config?: { results?: { opportunities?: { updated?: number }; cases?: { updated?: number } } };
}

interface ReshapeResult {
  opportunities?: { updated?: number };
  cases?: { updated?: number };
}

function loadHistory(): { query: string; time: string }[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? (JSON.parse(raw) as { query: string; time: string }[]) : [];
  } catch {
    return [];
  }
}

function saveHistory(history: { query: string; time: string }[]): void {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, HISTORY_MAX)));
  } catch {
    // localStorage may be disabled in private browsing; safe to ignore.
  }
}

function autoDetectKeys(
  data: Record<string, unknown>[] | null | undefined,
  xKey: string | null | undefined,
  yKey: string | null | undefined,
): { x: string; y: string } {
  if (!data || !data.length) return { x: xKey ?? "name", y: yKey ?? "value" };
  const keys = Object.keys(data[0]);
  const x = xKey && keys.includes(xKey)
    ? xKey
    : keys.find((k) => typeof data[0][k] === "string") ?? keys[0];
  const y = yKey && keys.includes(yKey)
    ? yKey
    : keys.find((k) => typeof data[0][k] === "number") ?? keys[1] ?? keys[0];
  return { x, y };
}

export function Explore(): JSX.Element {
  const auth = useAuth();
  const showToast = useUiStore((s) => s.showToast);
  const qc = useQueryClient();

  const [query, setQuery] = useState("");
  const [result, setResult] = useState<ExplorerResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<{ query: string; time: string }[]>(loadHistory);
  const [saving, setSaving] = useState(false);

  const [showReshape, setShowReshape] = useState(false);
  const [reshapeInput, setReshapeInput] = useState("");
  const [reshaping, setReshaping] = useState(false);
  const [reshapeError, setReshapeError] = useState<string | null>(null);
  const [reshapePreview, setReshapePreview] = useState<ReshapePreview | null>(null);
  const [reshapeResult, setReshapeResult] = useState<ReshapeResult | null>(null);

  const { data: savedQueries = [] } = useQuery({
    queryKey: ["explorer-saved"],
    queryFn: () => apiSoft<SavedQuery[]>("/api/explorer/saved").then((q) => q ?? []),
    enabled: auth.orgConnected,
  });

  // KI-1 (2026-04-20): block reshape while a provisioning pipeline is
  // in flight on this org. Reshape mutates the same SObjects the
  // provisioning job is writing — concurrent runs collide on
  // External_ID upsert, leave duplicate records, and corrupt the
  // demo story. Poll the active runs every 5s; if any are queued or
  // running we disable the Reshape button and surface a hint.
  const { data: activeRuns = [] } = useQuery({
    queryKey: ["runs-active-poll"],
    queryFn: api.listRuns,
    enabled: auth.orgConnected,
    refetchInterval: 5000,
  });
  const provisioningInFlight = useMemo(
    () => activeRuns.some((r) => r.status === "queued" || r.status === "running"),
    [activeRuns],
  );

  useEffect(() => {
    document.title = "Explore · Q Branch";
  }, []);

  const runQuery = async (q?: string): Promise<void> => {
    const text = (q ?? query).trim();
    if (!text) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiCall<ExplorerResult>("/api/explorer/query", {
        method: "POST",
        body: { query: text },
      });
      setResult(res);
      setHistory((prev) => {
        const next = [
          { query: text, time: new Date().toLocaleTimeString() },
          ...prev.filter((h) => h.query !== text),
        ].slice(0, HISTORY_MAX);
        saveHistory(next);
        return next;
      });
    } catch (e) {
      setError((e as Error).message || "Query failed");
    } finally {
      setLoading(false);
    }
  };

  const saveCurrent = async (): Promise<void> => {
    if (!result || result.error) return;
    const name = window.prompt(
      "Name for this query:",
      (query || result.explanation || "Saved query").slice(0, 50),
    );
    if (!name?.trim()) return;
    setSaving(true);
    try {
      await apiCall("/api/explorer/saved", {
        method: "POST",
        body: {
          name: name.trim(),
          nl_query: query || "",
          soql: result.soql,
          chart_type: result.chart_type,
        },
      });
      qc.invalidateQueries({ queryKey: ["explorer-saved"] });
      showToast("Query saved");
    } catch (e) {
      showToast(`Save failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  const deleteSaved = async (id: string): Promise<void> => {
    if (!window.confirm("Delete this saved query?")) return;
    try {
      await apiCall(`/api/explorer/saved/${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
      qc.invalidateQueries({ queryKey: ["explorer-saved"] });
    } catch (e) {
      showToast(`Delete failed: ${(e as Error).message}`);
    }
  };

  const previewReshape = async (): Promise<void> => {
    if (!reshapeInput.trim()) return;
    setReshaping(true);
    setReshapeError(null);
    try {
      const res = await apiCall<ReshapePreview>("/api/reshape/preview", {
        method: "POST",
        body: { nl_instructions: reshapeInput },
      });
      setReshapePreview(res);
    } catch (e) {
      setReshapeError((e as Error).message || "Preview failed");
    } finally {
      setReshaping(false);
    }
  };

  const applyReshape = async (): Promise<void> => {
    if (!reshapePreview) return;
    setReshaping(true);
    setReshapeError(null);
    try {
      const res = await apiCall<{ status?: string; job_id?: string; results?: ReshapeResult }>(
        "/api/reshape/reshape",
        {
          method: "POST",
          body: {
            overrides: reshapePreview.overrides,
            nl_instructions: "",
            target_stories: [],
          },
        },
      );
      if (res.status === "queued" && res.job_id) {
        for (let i = 0; i < 60; i++) {
          await new Promise((r) => setTimeout(r, 2000));
          const st = await apiCall<JobStatus>(`/api/jobs/${res.job_id}`);
          if (st.status === "completed") {
            setReshapeResult(st.config?.results ?? {});
            setReshapePreview(null);
            return;
          }
          if (st.status === "failed") {
            setReshapeError(st.error_message || "Reshape failed");
            return;
          }
        }
        setReshapeError("Reshape timed out after ~2 minutes");
      } else {
        setReshapeResult(res.results ?? {});
        setReshapePreview(null);
      }
    } catch (e) {
      setReshapeError((e as Error).message || "Reshape failed");
    } finally {
      setReshaping(false);
    }
  };

  if (auth.loading) {
    return <div style={{ padding: 60, textAlign: "center", color: "var(--ink-3)" }}>Loading…</div>;
  }
  if (!auth.orgConnected) {
    return (
      <div style={{ maxWidth: 480, margin: "60px auto", padding: 24, textAlign: "center" }}>
        <div className="card" style={{ padding: 32 }}>
          <h2 style={{ margin: 0, fontSize: 20, color: "var(--ink)" }}>Connect your org</h2>
          <p style={{ marginTop: 8, color: "var(--ink-2)", fontSize: 13 }}>
            Explore queries your provisioned demo data with AI. Connect a Salesforce org to get started.
          </p>
          <button
            className="btn primary"
            style={{ marginTop: 14 }}
            onClick={() => auth.connect()}
          >
            Connect org →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1280px] px-6 pb-32 pt-7">
      <MissionControlHero
        breadcrumb="Explore"
        title="Ask your demo data"
        subtitle="Natural-language SOQL · live results · reshape data with AI"
        iconGlyph={<span style={{ fontSize: 22 }}>✦</span>}
        chips={[
          {
            label: "Connected",
            dot: "#4BC076",
            bg: "rgba(75,192,118,.25)",
            color: "#A8E5BD",
          },
          ...(auth.org?.org_name
            ? [{ label: auth.org.org_name, bg: "rgba(255,255,255,.14)" }]
            : []),
        ]}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            background: "rgba(255,255,255,.08)",
            padding: 8,
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,.15)",
          }}
        >
          <span style={{ fontSize: 18, color: "white" }}>✦</span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void runQuery();
            }}
            placeholder="Ask a question… e.g. Show win rate by month"
            style={{
              flex: 1,
              background: "transparent",
              border: 0,
              color: "white",
              fontSize: 14,
              padding: "8px 4px",
              outline: "none",
            }}
          />
          <button
            type="button"
            disabled={loading || !query.trim()}
            onClick={() => void runQuery()}
            style={{
              background: "white",
              color: "#032d60",
              border: 0,
              padding: "6px 14px",
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: loading || !query.trim() ? "not-allowed" : "pointer",
              opacity: loading || !query.trim() ? 0.6 : 1,
            }}
          >
            {loading ? "Asking…" : "Ask →"}
          </button>
        </div>
      </MissionControlHero>

      {/* Quick chips */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "16px 0 12px" }}>
        <span
          style={{
            fontSize: 10,
            color: "var(--ink-3)",
            textTransform: "uppercase",
            letterSpacing: ".08em",
            alignSelf: "center",
            marginRight: 4,
            fontWeight: 600,
          }}
        >
          Quick:
        </span>
        {QUICK_CHIPS.map((sq) => (
          <button
            key={sq.label}
            className="chip ghost"
            style={{ cursor: "pointer", fontSize: 11 }}
            onClick={() => {
              setQuery(sq.query);
              void runQuery(sq.query);
            }}
          >
            {sq.label}
          </button>
        ))}
      </div>

      {/* Suggested when no result */}
      {!result && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 18 }}>
          {SUGGESTED.map((sq) => (
            <button
              key={sq.label}
              className="chip ghost"
              style={{ cursor: "pointer", fontSize: 11 }}
              onClick={() => {
                setQuery(sq.query);
                void runQuery(sq.query);
              }}
            >
              {sq.label}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div
          className="card"
          style={{
            padding: 14,
            marginBottom: 14,
            borderColor: "var(--pink)",
            background: "#fdf3f4",
            color: "var(--pink)",
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {result && <ResultCard
        result={result}
        showReshape={showReshape}
        provisioningInFlight={provisioningInFlight}
        onToggleReshape={() => {
          if (provisioningInFlight) {
            showToast("A provisioning run is in flight — wait for it to finish before reshaping.");
            return;
          }
          setShowReshape((o) => !o);
          if (!reshapeInput && query) setReshapeInput(query);
        }}
        onSave={() => void saveCurrent()}
        saving={saving}
        reshapeInput={reshapeInput}
        setReshapeInput={setReshapeInput}
        reshaping={reshaping}
        reshapeError={reshapeError}
        reshapePreview={reshapePreview}
        reshapeResult={reshapeResult}
        onPreviewReshape={() => void previewReshape()}
        onApplyReshape={() => void applyReshape()}
        onResetReshape={() => {
          setReshapeResult(null);
          setReshapePreview(null);
          setReshapeInput("");
          setReshapeError(null);
        }}
        onRerunQuery={() => {
          setShowReshape(false);
          setReshapeResult(null);
          setReshapePreview(null);
          if (query) void runQuery(query);
        }}
      />}

      {/* Saved + history side-by-side */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 16 }}>
        <div className="card" style={{ padding: 14 }}>
          <div className="label" style={{ marginBottom: 8 }}>Saved queries</div>
          {savedQueries.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
              No saved queries yet — run something and click "Save".
            </div>
          ) : (
            <div style={{ display: "grid", gap: 6 }}>
              {savedQueries.map((q) => (
                <div
                  key={q.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "6px 8px",
                    border: "1px solid var(--bd-2)",
                    borderRadius: 6,
                    background: "white",
                  }}
                >
                  <button
                    onClick={() => {
                      const text = q.nl_query || q.name;
                      setQuery(text);
                      void runQuery(text);
                    }}
                    style={{
                      flex: 1,
                      textAlign: "left",
                      background: "transparent",
                      border: 0,
                      color: "var(--ink)",
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: "pointer",
                      padding: 0,
                    }}
                    title={q.soql ?? ""}
                  >
                    {q.name}
                  </button>
                  <button
                    onClick={() => void deleteSaved(q.id)}
                    style={{
                      background: "transparent",
                      border: 0,
                      color: "var(--ink-3)",
                      fontSize: 11,
                      cursor: "pointer",
                    }}
                    title="Delete saved query"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="card" style={{ padding: 14 }}>
          <div
            className="label"
            style={{
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>History</span>
            {history.length > 0 && (
              <button
                onClick={() => {
                  setHistory([]);
                  saveHistory([]);
                }}
                style={{
                  background: "transparent",
                  border: 0,
                  color: "var(--ink-3)",
                  fontSize: 10,
                  cursor: "pointer",
                  textTransform: "none",
                }}
              >
                Clear
              </button>
            )}
          </div>
          {history.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
              Recent questions will appear here.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 4 }}>
              {history.slice(0, 10).map((h, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setQuery(h.query);
                    void runQuery(h.query);
                  }}
                  style={{
                    textAlign: "left",
                    background: "transparent",
                    border: 0,
                    padding: "4px 6px",
                    fontSize: 12,
                    color: "var(--ink-2)",
                    cursor: "pointer",
                    borderRadius: 4,
                  }}
                  className="hover-bg"
                >
                  <span style={{ color: "var(--ink-3)", marginRight: 6 }}>{h.time}</span>
                  {h.query}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface ResultCardProps {
  result: ExplorerResult;
  showReshape: boolean;
  provisioningInFlight: boolean;
  onToggleReshape: () => void;
  onSave: () => void;
  saving: boolean;
  reshapeInput: string;
  setReshapeInput: (s: string) => void;
  reshaping: boolean;
  reshapeError: string | null;
  reshapePreview: ReshapePreview | null;
  reshapeResult: ReshapeResult | null;
  onPreviewReshape: () => void;
  onApplyReshape: () => void;
  onResetReshape: () => void;
  onRerunQuery: () => void;
}

function ResultCard({
  result,
  showReshape,
  provisioningInFlight,
  onToggleReshape,
  onSave,
  saving,
  reshapeInput,
  setReshapeInput,
  reshaping,
  reshapeError,
  reshapePreview,
  reshapeResult,
  onPreviewReshape,
  onApplyReshape,
  onResetReshape,
  onRerunQuery,
}: ResultCardProps): JSX.Element {
  const { x, y } = autoDetectKeys(result.chart_data, result.x_key, result.y_key);
  return (
    <div className="card" style={{ padding: 0, overflow: "hidden" }}>
      {result.error && (
        <div
          style={{
            padding: "10px 16px",
            background: "#fdf3f4",
            borderBottom: "1px solid #f1c4c8",
            color: "var(--pink)",
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          {result.explanation || result.error}
        </div>
      )}
      {result.explanation && !result.error && (
        <div
          style={{
            padding: "10px 16px",
            background: "var(--cb-95)",
            borderBottom: "1px solid var(--bd-2)",
            color: "var(--ink)",
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>✦</span>
          <span>{result.explanation}</span>
        </div>
      )}
      {result.chart_data && result.chart_type && (
        <div style={{ padding: 16, height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            {result.chart_type === "bar" ? (
              <BarChart data={result.chart_data}>
                <XAxis dataKey={x} tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <ReTooltip />
                <Bar dataKey={y} fill="#066AFE" radius={[4, 4, 0, 0]} />
              </BarChart>
            ) : result.chart_type === "line" ? (
              <LineChart data={result.chart_data}>
                <XAxis dataKey={x} tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <ReTooltip />
                <Line
                  type="monotone"
                  dataKey={y}
                  stroke="#066AFE"
                  strokeWidth={2}
                />
              </LineChart>
            ) : result.chart_type === "pie" ? (
              <PieChart>
                <Pie
                  data={result.chart_data}
                  dataKey={y}
                  nameKey={x}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label
                >
                  {result.chart_data.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <ReTooltip />
                <Legend />
              </PieChart>
            ) : (
              <></>
            )}
          </ResponsiveContainer>
        </div>
      )}
      {result.narrative && (
        <div
          style={{
            padding: "10px 16px",
            borderTop: "1px solid var(--bd-2)",
            color: "var(--ink-2)",
            fontSize: 13,
            lineHeight: 1.5,
            display: "flex",
            gap: 8,
            alignItems: "flex-start",
          }}
        >
          <span style={{ marginTop: 1 }}>✦</span>
          <span>{result.narrative}</span>
        </div>
      )}
      {result.table_data && result.table_data.length > 0 && (
        <div style={{ borderTop: "1px solid var(--bd-2)", overflow: "auto" }}>
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--cb-95)" }}>
                {Object.keys(result.table_data[0]).map((col) => (
                  <th
                    key={col}
                    style={{
                      textAlign: "left",
                      padding: "8px 12px",
                      fontSize: 10,
                      textTransform: "uppercase",
                      letterSpacing: ".08em",
                      color: "var(--ink-3)",
                    }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.table_data.slice(0, 20).map((row, i) => (
                <tr key={i} style={{ borderTop: "1px solid var(--bd-2)" }}>
                  {Object.values(row).map((val, j) => (
                    <td
                      key={j}
                      style={{ padding: "6px 12px", color: "var(--ink-2)" }}
                    >
                      {typeof val === "number" ? val.toLocaleString() : String(val ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {result.table_data.length > 20 && (
            <div style={{ padding: "6px 12px", fontSize: 10, color: "var(--ink-3)" }}>
              Showing 20 of {result.table_data.length} rows
            </div>
          )}
        </div>
      )}
      {result.soql && <SoqlBlock soql={result.soql} />}
      {!result.error && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 16px",
            borderTop: "1px solid var(--bd-2)",
            background: "var(--cb-98)",
          }}
        >
          <button
            onClick={onSave}
            disabled={saving}
            style={{
              background: "transparent",
              border: 0,
              color: "var(--teal)",
              fontSize: 12,
              cursor: saving ? "not-allowed" : "pointer",
              fontWeight: 600,
            }}
          >
            {saving ? "Saving…" : "💾 Save query"}
          </button>
          <button
            onClick={onToggleReshape}
            disabled={provisioningInFlight}
            title={
              provisioningInFlight
                ? "A provisioning run is in flight — wait for it to finish before reshaping (concurrent writes corrupt the demo data)."
                : undefined
            }
            style={{
              background: "transparent",
              border: 0,
              opacity: provisioningInFlight ? 0.45 : 1,
              cursor: provisioningInFlight ? "not-allowed" : "pointer",
              color: "var(--violet)",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            ⚡ {showReshape ? "Hide reshape" : "Reshape this data"}
          </button>
        </div>
      )}

      {showReshape && (
        <div
          style={{
            padding: 16,
            borderTop: "1px solid var(--bd-2)",
            background: "rgba(168, 110, 233, .05)",
          }}
        >
          {reshapeResult ? (
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--teal)" }}>
                ✓ Data reshaped
              </div>
              <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 4 }}>
                Updated {reshapeResult.opportunities?.updated ?? 0} opportunities ·{" "}
                {reshapeResult.cases?.updated ?? 0} cases
              </div>
              <div style={{ marginTop: 10, display: "flex", gap: 10 }}>
                <button
                  onClick={onResetReshape}
                  style={{
                    background: "transparent",
                    border: 0,
                    color: "var(--violet)",
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  Reshape again
                </button>
                <button
                  onClick={onRerunQuery}
                  style={{
                    background: "transparent",
                    border: 0,
                    color: "var(--teal)",
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  Re-run query to see changes
                </button>
              </div>
            </div>
          ) : reshapePreview ? (
            <div style={{ display: "grid", gap: 10 }}>
              <div style={{ fontSize: 12, color: "var(--ink)", fontWeight: 600 }}>
                Review changes before applying:
              </div>
              <div
                style={{
                  background: "white",
                  border: "1px solid var(--bd-2)",
                  borderRadius: 6,
                  padding: 10,
                }}
              >
                <div style={{ fontSize: 12, color: "var(--ink)" }}>
                  {reshapePreview.interpretation}
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 14,
                    marginTop: 6,
                    fontSize: 10,
                    color: "var(--ink-3)",
                  }}
                >
                  <span>~{reshapePreview.opp_count} opportunities affected</span>
                  <span>~{reshapePreview.case_count} cases affected</span>
                </div>
              </div>
              {reshapeError && (
                <div style={{ fontSize: 12, color: "var(--pink)" }}>{reshapeError}</div>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  className="btn primary sm"
                  onClick={onApplyReshape}
                  disabled={reshaping}
                >
                  {reshaping ? "Applying…" : "Apply changes"}
                </button>
                <button
                  className="btn ghost sm"
                  onClick={onResetReshape}
                  disabled={reshaping}
                >
                  Back
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)" }}>
                Describe how you want to change the data:
              </div>
              <textarea
                value={reshapeInput}
                onChange={(e) => setReshapeInput(e.target.value)}
                placeholder="e.g. Increase win rates to 65%, add more closed-lost in Q1, make ACME the biggest account…"
                rows={3}
                style={{
                  width: "100%",
                  padding: 10,
                  border: "1px solid var(--bd)",
                  borderRadius: 6,
                  fontSize: 12,
                  fontFamily: "inherit",
                  resize: "vertical",
                  background: "white",
                  color: "var(--ink)",
                }}
              />
              {reshapeError && (
                <div style={{ fontSize: 12, color: "var(--pink)" }}>{reshapeError}</div>
              )}
              <button
                className="btn primary sm"
                onClick={onPreviewReshape}
                disabled={reshaping || !reshapeInput.trim()}
                style={{ alignSelf: "flex-start", background: "var(--violet)" }}
              >
                {reshaping ? "Previewing…" : "✦ Preview changes"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Collapsible SOQL display with a one-click copy button. SEs paste this
// query into Workbench constantly — saving them the manual select is a
// 10-second-a-day quality-of-life win.
function SoqlBlock({ soql }: { soql: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const onCopy = async (e: React.MouseEvent): Promise<void> => {
    // Stop the click from toggling the parent <details>.
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(soql);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard unavailable — silently no-op */
    }
  };
  return (
    <details
      style={{ borderTop: "1px solid var(--bd-2)", padding: "8px 16px" }}
    >
      <summary
        style={{
          fontSize: 11,
          color: "var(--ink-3)",
          cursor: "pointer",
          userSelect: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <span>View SOQL query</span>
        <button
          type="button"
          onClick={onCopy}
          style={{
            fontSize: 10,
            padding: "3px 8px",
            border: "1px solid var(--bd)",
            background: copied ? "var(--chip-green-bg)" : "white",
            color: copied ? "var(--chip-green-fg)" : "var(--ink-2)",
            borderRadius: 4,
            cursor: "pointer",
            fontWeight: 600,
          }}
          aria-label="Copy SOQL to clipboard"
        >
          {copied ? "Copied ✓" : "Copy"}
        </button>
      </summary>
      <pre
        style={{
          marginTop: 8,
          padding: 10,
          background: "#0f1e36",
          color: "#d8e3f3",
          borderRadius: 6,
          fontSize: 11,
          fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
          overflow: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {soql}
      </pre>
    </details>
  );
}
